import { useState, useCallback } from "react";
import {
  Box,
  BoxProps,
  Button,
  IconButton,
  Popover,
  Stack,
  Radio,
  RadioGroup,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Divider,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import LoadingButton from "@mui/lab/LoadingButton";
import { IssueResolutionType } from "../../fixtures";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveIssue } from "./mutations";

export interface Props extends BoxProps {
  issueId: string;
  disabled?: boolean;
}

export default function ResolveButton({
  issueId,
  disabled = false,
  ...etc
}: Props) {
  const queryClient = useQueryClient();

  const [el, setEl] = useState<HTMLElement | null>(null);

  const [selectedResolution, setSelectedResolution] = useState("");
  const [summary, setSummary] = useState("");

  const incomplete = !summary || !selectedResolution;

  const onCancel = useCallback(() => {
    setEl(null);
    setSelectedResolution("");
    setSummary("");
  }, []);

  const resolveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedResolution) {
        throw new Error("Bad resolution type");
      }

      if (!summary) {
        throw new Error("Missing resolution summary");
      }

      return resolveIssue(
        issueId,
        selectedResolution as IssueResolutionType,
        summary,
      );
    },
    mutationKey: ["issues", "resolve", issueId],
    onSuccess: () => {
      setEl(null);
      setSelectedResolution("");
      setSummary("");

      queryClient.invalidateQueries({
        queryKey: ["issueActivity", issueId],
      });

      queryClient.invalidateQueries({
        queryKey: ["getIssue", issueId],
      });

      queryClient.invalidateQueries({
        queryKey: ["my", "issues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["all", "issues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["getMonitorIssues"],
      });

      queryClient.invalidateQueries({
        queryKey: ["my", "issues", "openIssueCount"],
      });

      queryClient.invalidateQueries({
        queryKey: ["all", "issues", "openIssueCount"],
      });

      // TODO: optimistic updates
    },
    onError: (error, variables, context) => {},
  });

  const alreadyResolved = false;

  return (
    <Box {...etc}>
      <IconButton
        sx={{
          display: "flex",
          flexShrink: 0,
        }}
        disabled={disabled || alreadyResolved}
        onClick={(e) => {
          setEl(e.currentTarget);
        }}
      >
        <ArchiveIcon />
      </IconButton>
      <Popover
        open={!disabled && el != null}
        anchorEl={el}
        onClose={resolveMutation.isPending ? undefined : onCancel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack sx={{ padding: "1em", width: "300px" }}>
          <FormControl>
            <FormLabel>Action to resolve this issue (required)</FormLabel>
            <RadioGroup
              value={selectedResolution}
              onChange={(e) => {
                setSelectedResolution(e.target.value);
              }}
            >
              <FormControlLabel
                value={IssueResolutionType.Cleared}
                control={<Radio />}
                label="Cleared"
              />
              <FormControlLabel
                value={IssueResolutionType.TerminatedMerchant}
                control={<Radio />}
                label="Terminated Merchant"
              />
              <FormControlLabel
                value={IssueResolutionType.NoticeSent}
                control={<Radio />}
                label="Notice Sent"
              />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ mt: "1em" }}>
            <FormLabel>Summary of resolution (required)</FormLabel>
            <TextField
              multiline
              rows={3}
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
              }}
            />
          </FormControl>
          {/* TODO: display error Alert */}
          <div
            style={{
              display: "flex",
              gap: "1em",
              justifyContent: "end",
              marginTop: "1em",
            }}
          >
            <Button
              variant="outlined"
              disabled={resolveMutation.isPending}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={resolveMutation.isPending}
              disabled={incomplete}
              variant="contained"
              onClick={async () => {
                await resolveMutation.mutateAsync();
              }}
            >
              Resolve Issue
            </LoadingButton>
          </div>
        </Stack>
      </Popover>
    </Box>
  );
}
